import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PregnancyCareSection = () => {

  const arrowRef = useRef(null);

  // 2. Track the scroll progress of that specific element
  const { scrollYProgress } = useScroll({
    target: arrowRef,
    // "start 90%" -> Starts animation when the top of the element hits 90% down the screen
    // "end 60%" -> Finishes animation when the bottom of the element hits 60% down the screen
    offset: ["start 90%", "end 60%"] 
  });

  // 3. Map the scroll progress (0 to 1) to the clipPath percentage (100% to 0%)
  const drawClipPath = useTransform(
    scrollYProgress, 
    [0, 1], 
    ["inset(0 100% 0 0)", "inset(0 0% 0 0)"]
  );
  
  const carePoints = [
    {
      id: "top-left",
      title: 'The "15-Minute" Rush',
      desc: "Tired of hurried checkups that leave your deepest questions unanswered?",
      img: "/keepingup1.png",
      dotPath: "/keepinigkdjbf.png",
      rotation: "rotate(180deg) scaleX(-1)", 
      initialX: -100, 
    },
    {
      id: "top-right",
      title: 'The Gynecologist "Island"',
      desc: "Standard care stops at the clinic; you're left without guidance for nutrition.",
      img: "/keepingup2.png",
      dotPath: "/keepiingdjhfv.png",
      rotation: "rotate(180deg)",
      initialX: 100,
    },
    {
      id: "bottom-left",
      title: "Fragmented Advice",
      desc: "Searching the internet at 2 AM because there's no expert to call?",
      img: "/keepingup3.png",
      dotPath: "/keepingskjfn.png",
      rotation: "scaleX(-1)",
      initialX: -100,
    },
    {
      id: "bottom-right",
      title: "The Management Chaos",
      desc: "Tracking reports alone? You need a partner with tech, not just a doctor.",
      img: "/keepingup4.png",
      dotPath: "/keepingjhdfbv.png",
      rotation: "none",
      initialX: 100,
    },
  ];

  return (
    <section className="pregnancy-care-wrapper">
      <div className="care-header text-center mb-5">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          Is your <span className="highlight-pink">pregnancy care</span> keeping up with you?
        </motion.h2>
        <p className="journey-subtitle">
              You’re managing a career, a home, and a future—yet standard pregnancy care
              still feels stuck in the past. We listened to thousands of mothers who felt the same "void"
              between doctor visits.
            </p>
      </div>

      <div className="care-visual-container">
        <motion.div 
          className="center-woman-box"
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ amount: 0.3 }}
        >
          <motion.img 
            src="/skljfhnbjkkeepingupC.png" 
            className="thinking-cloud d-none d-lg-block"
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
          />
          <motion.img
            src="/skljfhnbjkkeepingupC.png"
            className="thinking-cloud-mobile d-lg-none"
            alt=""
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
          />
          <img src="/keepingupM0.png" className="center-img" alt="Main" />
        </motion.div>

        {carePoints.map((point, index) => (
          <React.Fragment key={point.id}>
            <motion.img
              src={point.dotPath}
              className={`dot-connector path-${point.id} d-none d-lg-block`}
              style={{ transform: point.rotation }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.6 }}
              transition={{ delay: 0.4 }}
            />

            <motion.div 
              className={`care-card floating-node ${point.id}`}
              style={{ animationDelay: `${index * 0.35}s` }}
              initial={{ opacity: 0, x: typeof window !== 'undefined' && window.innerWidth > 992 ? point.initialX : 0, y: 30 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.1 }}
              viewport={{ once: false, amount: 0.2 }}
            >
              <div className="card-img-box">
                <img src={point.img} alt={point.title} />
                <div className="card-text-overlay">
                  <h3>{point.title}</h3>
                  <p>{point.desc}</p>
                </div>
              </div>
            </motion.div>

            {index < carePoints.length - 1 && (
              <div className="mobile-footprints d-lg-none">
                <img src="/footprintsakdsbfh.png" className="footprint-img" alt="step" />
                <div className="vertical-dotted-line"></div>
                <img src="/footprintsakdsbfh.png" className="footprint-img alt-foot" alt="step" />
              </div>
            )}
          </React.Fragment>
        ))}

        <motion.div 
          className="heart-line-box"
          ref={arrowRef} // <-- Attach the ref here
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          {/* Apply the dynamic scroll value directly to the style */}
          <motion.img 
            src="/vgjghvjghvjkeepingH.png" 
            className="heart-svg" 
            alt="heart path" 
            style={{ clipPath: drawClipPath }} // <-- Magic happens here
          />
          
          <span className="hindi-text">"सिर्फ एक सफ़र नहीं, एक खूबसूरत उत्सव"</span>
        </motion.div>
      </div>
    </section>
  );
};

export default PregnancyCareSection;
